import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { QueueingSubject } from 'queueing-subject';
import makeWebSocketObservable, {
    GetWebSocketResponses,
    normalClosureMessage // ,WebSocketPayload
} from 'rxjs-websockets';
import { BehaviorSubject, interval, Observable, Subject, Subscriber, Subscription } from 'rxjs/index';
import { delay, retryWhen, share, switchMap, takeUntil, takeWhile, withLatestFrom } from 'rxjs/operators';
import { AuthenticationActions } from '../../auth/state/authentication.actions';
import { AuthState } from '../../auth/state/authentication.reducer';
import { AuthenticationSelectors } from '../../auth/state/authentication.selectors';

type WebSocketPayload = string | ArrayBuffer | Blob; // This type is for some reason not exported by rxjs-websockets

// TODO: Consider adding headers, security token, etc.
export interface WebSocketMessage {
    action?: string;
    topic?: string;
    body: any;
}

export class RemoteSubject<T> extends Subject<T> {
    first = true;
    initialBody: any;

    constructor(
        private websocketBrokerService: WebsocketBrokerService,
        private topic: string) {
        super();
    }

    setInitialBody(initialBody: any): void {
        this.initialBody = initialBody;
    }

    _subscribe(subscriber: Subscriber<T>): Subscription {
        const subscription = super._subscribe(subscriber); // Using .subscribe() causes "maximum call stack size exceeded" error
        if (this.first) {
            this.websocketBrokerService.sendAction(
                'subscribe',
                this.topic,
                this.initialBody === undefined
                    ? 'Setup stream process - no initial body' : this.initialBody);
            this.first = false;
        }

        return subscription;
    }
}

@Injectable({
    providedIn: 'root'
})
export class WebsocketBrokerService {

    // this subject queues as necessary to ensure every message is delivered
    input$ = new QueueingSubject<string>();
    socket$: Observable<any>;
    authenticate$: Observable<AuthState>;
    subscriptions: Subscription = new Subscription();
    gotFirstMessageFromBackend = false;

    messagesFromServer$: Observable<WebSocketPayload>;

    observers: Map<string, RemoteSubject<any>> = new Map();

    isUserAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    messagesSubscription: Subscription = new Subscription();

    constructor(private authenticationSelectors: AuthenticationSelectors,
                private store: Store<any>) {
        this.authenticate$ = authenticationSelectors.selectAuthenticate();

        const protocol = location.protocol.replace(/^http/, 'ws');
        const port =  location.port ? `:${location.port}` : '';
        const url = `${protocol}//${location.hostname}${port}/servlets/ws`;

        if (window.self === window.top) {
           this.socket$ = makeWebSocketObservable(url);

           // This does NOT create connection - only on subscription
           this.messagesFromServer$ = this.socket$
              .pipe(
                 // The observable produces a value once the websocket has been opened
                 switchMap((getResponses: GetWebSocketResponses) => {
                    console.log('websocket opened - getting observable to messages');

                    return getResponses(this.input$); // binding to input queue subject
                 }),
                 share(), // Allowing multiple subscribes to share same connection
                 retryWhen(errors => errors
                    .pipe(
                       takeUntil(this.isUserAuthenticated$),
                       delay(5000)
                    ))
                 // If connection broken because server restarted then user needs to refresh session anyway
                 // - so just wait for dialog to display - but consider if we should delay longer in that case
              );

           this.subscriptions.add(this.authenticate$
              // .pipe(filter(authenticate => authenticate.isUserAuthenticated))
              .subscribe(authenticate => {
                 this.isUserAuthenticated$.next(authenticate.isUserAuthenticated);

                 // to keep the websocket alive, we need to send a ping/heartbeat to backend at a regular interval
                 const subscription = interval(30000)
                    .pipe(
                       withLatestFrom(this.isUserAuthenticated$),
                       takeWhile(([_, isUserAuthenticated]) => isUserAuthenticated)
                    )
                    .subscribe(() => {
                       if (authenticate.viewSessionId !== undefined) {
                          this.sendMessage('heartbeat/', { viewSessionId: authenticate.viewSessionId });
                       }
                    });

                 this.subscriptions.add(subscription);

                 if (authenticate.isUserAuthenticated) {
                    // Queue first message for server - to create initial WebSocket connection
                    this.sendMessage('handshake/', { viewSessionId: authenticate.viewSessionId });

                    this.messagesSubscription = this.messagesFromServer$
                       .subscribe((message: string) => {
                             if (!this.gotFirstMessageFromBackend) {
                                // TODO: Check if message is in fact a handshake
                                console.log('First message from backend:', message);
                                // TODO: Should we send confirmation handshake back? What does STOMP do?
                                this.gotFirstMessageFromBackend = true;
                             } else {
                                if (message !== 'pong') {
                                   console.log('#### Message from backend:', message);
                                   const webSocketMessage: WebSocketMessage = JSON.parse(message);
                                   // Distribute message body to all subscribers of remote subject
                                   if (webSocketMessage.topic === 'handshake/') {
                                      this.sendMessage('handshake/', { viewSessionId: authenticate.viewSessionId });
                                      //  alert('Connection Interrupted. Reconnecting now ...');
                                      if (this.observers.size > 0) {
                                         // This is required to reconnect all obeservers
                                         this.observers.forEach((observer: any) => {
                                            const topic = observer.topic;
                                            const initialBody = observer.initialBody;
                                            this.sendAction(
                                               'subscribe',
                                               topic,
                                               initialBody === undefined
                                                  ? 'Setup stream process - no initial body' : initialBody);
                                         });
                                      }
                                   } else if (webSocketMessage.topic === 'sessionTimeout/') {
                                      this.messagesSubscription.unsubscribe();
                                      this.store.dispatch(AuthenticationActions.websocketSessionTimeout());
                                   } else {
                                      const subject = this.observers.get(webSocketMessage.topic);
                                      if (subject) {
                                         subject.next(webSocketMessage.body);
                                      }
                                   }
                                }
                             }
                          },
                          (error: Error) => {
                             const { message } = error;
                             if (message === normalClosureMessage) {
                                console.log('Server closed the websocket connection normally');
                             } else {
                                console.log('Socket was disconnected due to error:', message);
                             }
                          },
                          () => {
                             // The clean termination only happens in response to the last
                             // subscription to the observable being unsubscribed, any
                             // other closure is considered an error.
                             console.log('The connection was closed in response to the user');

                             // TODO: find a better way to handle reconnect websockets, when connection is lost
                             if (this.isUserAuthenticated$.getValue()) {
                                this.store.dispatch(AuthenticationActions.getLatestPortalVersion());
                             }
                          }
                       );
                    this.subscriptions.add(this.messagesSubscription);
                 } else {
                    // unsub if already used?
                 }
              })
           );
        }
    }

    sendAction(action: string, topic: string, body: any): void {
        const message: WebSocketMessage = {
            action,
            topic,
            body
        };
        this.input$.next(JSON.stringify(message));

        // This is required to set the data to current data at the time of reconnecting
        const subject = this.observers.get(topic);
        if (subject !== undefined) {
            subject.setInitialBody(body);
            this.observers.set(topic, subject);
        }
    }

    sendMessage(topic: string, body: any): void {
        const message: WebSocketMessage = {
            topic,
            body
        };
        this.input$.next(JSON.stringify(message));
    }

    unsubscribe(topic: string): void {
        this.sendAction(
            'unsubscribe',
            topic,
            'Stopping stream process');

        // This is required to resubscribe to the topic again
        this.observers.delete(topic);
    }

    getRemoteObservable(topic: string, initialBody?: any): Observable<any> {
        let subject = this.observers.get(topic);

        if (subject === undefined) {
            // On first subscription the subject will send message to server to setup the streaming process
            subject = new RemoteSubject(this, topic);
            if (initialBody) {
                subject.setInitialBody(initialBody);
            }
            this.observers.set(topic, subject);
        }

        return subject.asObservable();
    }
}
